import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Segmented } from '@/components/synapse/Segmented';
import { LaboratoriesSection } from '@/features/lab/LaboratoriesSection';
import { TestListSection } from '@/features/lab/TestListSection';

// The tabs reuse the two sections' own titles rather than a parallel pair of
// keys — one name per list, so a rename cannot leave the tab disagreeing with
// what it opens.
const TABS = [
  { value: 'tests' as const, labelKey: 'lab.catalogue.title' },
  { value: 'laboratories' as const, labelKey: 'lab.laboratories.title' },
];
type ConfigTab = (typeof TABS)[number]['value'];

/**
 * The lab desk's reference data, in one place behind `lab.catalogue.manage`: the
 * tests the clinic offers, and the laboratories it sends work to.
 *
 * Two tabs rather than two stacked sections — the lists are read at different
 * times, and the test list is long enough that anything below it is effectively
 * hidden. Two nav leaves were the other option and were rejected: one permission,
 * one destination.
 *
 * The tab is local state, not a URL segment. Nothing links to a specific tab, and
 * `/lab/catalogue` stays the route so existing bookmarks keep working even though
 * the page is now broader than the catalogue.
 */
export function LabConfigPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<ConfigTab>('tests');

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5 px-4 py-6">
      <header>
        <h1 className="text-xl font-semibold tracking-[-0.02em] text-ink">
          {t('lab.config.title')}
        </h1>
        <p className="text-[13px] text-muted">{t('lab.config.subtitle')}</p>
      </header>

      <Segmented
        value={tab}
        onChange={setTab}
        options={TABS}
        ariaLabel={t('lab.config.tabsLabel')}
      />

      {/* Unmounted rather than hidden: each section owns its own draft state, and
          a half-typed row left behind a tab is a surprise on the way back. */}
      {tab === 'tests' ? <TestListSection /> : <LaboratoriesSection />}
    </div>
  );
}
